import { useEffect, useState } from 'react';
import './strategy-panel.styles.scss';
import { DEFENSES, DEPTHS, OFFENSES, PACES, cleanStrategy, rosterFit } from '@shared/strategy.js';
import { UseCloudFunction } from '@/utils/firebase.utils';
import HelpLink from '@/components/SHARED/help-link/help-link.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

const GROUPS = [
    { cat: 'offense', title: 'Offense', list: OFFENSES },
    { cat: 'defense', title: 'Defense', list: DEFENSES },
    { cat: 'pace', title: 'Pace', list: PACES },
    { cat: 'depth', title: 'Rotation', list: DEPTHS },
];

// The coach's usual strategy: offense, defense, pace and rotation depth.
// Each option shows how the roster suits it (visible ratings) and, once the
// team has run it, how the players looked in it (team.systemReads, words
// only). It's saved in the private plans document (plans.usual); teams
// that haven't saved one yet run what they ran last (team.strategy).
const StrategyPanel = ({ league, team, plans }) => {

    const saved = cleanStrategy(plans?.usual ?? team.strategy);
    const savedKey = JSON.stringify(saved);
    const [ strategy, setStrategy ] = useState(saved);
    const [ saving, setSaving ] = useState(false);
    const [ message, setMessage ] = useState(null);

    useEffect(() => {
        setStrategy(JSON.parse(savedKey));
    }, [savedKey])

    const fit = rosterFit(team.players, { ratingMean: league.settings.talent?.ratingMean ?? 50 });
    const dirty = JSON.stringify(strategy) !== savedKey;

    async function save() {
        setSaving(true);
        setMessage(null);
        const res = await UseCloudFunction('saveStrategy', { leagueId: league.id, strategy });
        setSaving(false);
        setMessage(res.error ? { error: true, text: res.error } : { text: 'Strategy saved. It starts with the next game that has no game plan of its own.' });
    }

    return (
        <div className='g-card strategy-panel'>
            <h2>Usual strategy <HelpLink section='strategy' label='How strategy works' /></h2>
            <p className='meta'>
                <b>Roster fit</b> is how well your players&apos; ratings suit each choice.
                Every team also has a feel for some systems that ratings don&apos;t show: <b>what we&apos;ve seen</b> sums up
                how your players have looked running each one, and gets clearer the longer you run it.
            </p>
            {GROUPS.map(({ cat, title, list }) => (
                <fieldset key={cat} className='strategy-group'>
                    <legend>{title}</legend>
                    <div className='strategy-options'>
                        {list.map(o => {
                            const chosen = strategy[cat] === o.key;
                            const score = fit[cat]?.[o.key] ?? 0;
                            const read = team.systemReads?.[cat]?.[o.key];
                            return (
                                <label key={o.key} className={`strategy-option ${chosen ? 'chosen' : ''}`}>
                                    <input type='radio' name={`strategy-${cat}`} value={o.key} checked={chosen}
                                        onChange={() => { setStrategy({ ...strategy, [cat]: o.key }); setMessage(null); }} />
                                    <span className='option-head'>
                                        <b>{o.label}</b>
                                        {score >= 0.25 && <span className='tag good'>Good roster fit</span>}
                                        {score <= -0.25 && <span className='tag poor'>Poor roster fit</span>}
                                    </span>
                                    <span className='meta option-text'>{o.text}</span>
                                    {cat !== 'depth' && <ReadLine read={read} />}
                                </label>
                            )
                        })}
                    </div>
                </fieldset>
            ))}
            <div className='strategy-actions'>
                <span role='status' className={message?.error ? 'error' : 'meta'}>
                    {message ? message.text : dirty ? 'You have unsaved changes.' : plans?.usual || team.strategySavedAt ? 'Saved.' : 'Your team is running the default strategy.'}
                </span>
                <div className='strategy-buttons'>
                    <button className='g-button' disabled={!dirty || saving} onClick={() => { setStrategy(saved); setMessage(null); }}>Undo</button>
                    <button className={`g-button primary ${!dirty || saving ? 'disabled' : ''}`} disabled={!dirty || saving} onClick={save}>
                        {saving ? <ThreeDotsLoader /> : 'Save strategy'}
                    </button>
                </div>
            </div>
        </div>
    )
}

const LOOKS = { good: 'Players look comfortable', mixed: 'Mixed results', poor: 'Players look uncomfortable' };

// What the coach has seen running a system: nothing yet, too early, or a
// look (early signs after 2-4 games, clear after 5).
const ReadLine = ({ read }) => {
    if (!read?.games) return <span className='read none'>Not tried yet</span>;
    const games = `${read.games} game${read.games === 1 ? '' : 's'}`;
    if (!read.look) return <span className='read early'>Too early to tell ({games})</span>;
    return (
        <span className={`read ${read.look}`}>
            {LOOKS[read.look]} <span className='meta'>({read.sure === 'clear' ? 'clear' : 'early signs'}, {games})</span>
        </span>
    );
};

export default StrategyPanel
